import * as THREE from 'three';

/** Diagnostic switch used by same-frame image/performance checks. */
export const SCATTER_BATCHING = { enabled: true };
interface Group { owner: THREE.Object3D; sources: THREE.InstancedMesh[]; count: number }
interface Entry { mesh: THREE.InstancedMesh; signature: string; last: number; capacity: number; release: () => void }
const identity = new THREE.Matrix4();

/** Combine compatible, already-culled scatter draws. Placements and geometry
 * remain owned by their terrain tiles; these are temporary render proxies.
 * Each depth/reflection pass has its own instance buffers because Three uploads
 * an InstancedMesh's attributes at most once per renderer frame. */
export class ScatterBatchPass {
  private readonly entries = new Map<string, Entry>();
  private readonly groups = new Map<string, Group>();
  private readonly hidden: THREE.InstancedMesh[] = [];
  private readonly mounted: THREE.InstancedMesh[] = [];
  private generation = 0;

  add(mesh: THREE.InstancedMesh, owner: THREE.Object3D, depth = 0): void {
    const material = mesh.material;
    if (!mesh.count || Array.isArray(material) || material.transparent || !mesh.matrix.equals(identity)
      || mesh.onBeforeRender !== THREE.Object3D.prototype.onBeforeRender
      || mesh.onBeforeShadow !== THREE.Object3D.prototype.onBeforeShadow
      || mesh.onAfterRender !== THREE.Object3D.prototype.onAfterRender
      || mesh.onAfterShadow !== THREE.Object3D.prototype.onAfterShadow
      || mesh.morphTexture || mesh.renderOrder !== 0) return;
    // Keep close foliage in short depth bands so batching does not destroy the
    // renderer's front-to-back order and turn saved draws into extra overdraw.
    const band = depth < 600 ? Math.floor(Math.max(0, depth) / 100) : 6 + Math.floor((depth - 600) / 1000);
    const key = `${owner.id}/${band}/${mesh.geometry.id}/${material.id}/${mesh.customDepthMaterial?.id ?? 0}/${mesh.customDistanceMaterial?.id ?? 0}/${+mesh.castShadow}/${+mesh.receiveShadow}/${mesh.layers.mask}/${+!!mesh.instanceColor}`;
    let group = this.groups.get(key);
    if (!group) { group = { owner, sources: [], count: 0 }; this.groups.set(key, group); }
    group.sources.push(mesh); group.count += mesh.count;
  }

  begin(scene: THREE.Scene): void {
    this.generation++;
    for (const [key, group] of this.groups) {
      if (group.sources.length < 2) continue;
      const source = group.sources[0];
      let entry = this.entries.get(key);
      if (!entry || entry.capacity < group.count) {
        entry?.release();
        const capacity = 2 ** Math.ceil(Math.log2(group.count));
        const mesh = new THREE.InstancedMesh(source.geometry, source.material, capacity);
        mesh.name = 'scatter-pass-batch';
        mesh.matrixAutoUpdate = false; mesh.matrixWorldAutoUpdate = false;
        mesh.castShadow = source.castShadow; mesh.receiveShadow = source.receiveShadow;
        mesh.layers.mask = source.layers.mask;
        mesh.customDepthMaterial = source.customDepthMaterial; mesh.customDistanceMaterial = source.customDistanceMaterial;
        mesh.boundingSphere = new THREE.Sphere();
        mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        if (source.instanceColor) mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3).setUsage(THREE.DynamicDrawUsage);
        const u = source.userData;
        mesh.userData.censusKind = u.rockGroups ? 'rock' : u.alpineCover || u.meadowCover || u.forestFloor || u.wetlandCover || u.coastalCover ? 'cover' : 'tree';
        const resources = [source.geometry, source.material as THREE.Material, source.customDepthMaterial, source.customDistanceMaterial].filter(Boolean) as (THREE.BufferGeometry | THREE.Material)[];
        const release = () => {
          for (const resource of resources) resource.removeEventListener('dispose', release);
          mesh.dispose(); this.entries.delete(key);
        };
        for (const resource of resources) resource.addEventListener('dispose', release);
        entry = { mesh, signature: '', last: this.generation, capacity, release }; this.entries.set(key, entry);
      }
      const mesh = entry.mesh;
      const signature = group.sources.map(s => `${s.id}:${s.count}:${s.instanceMatrix.version}:${s.instanceColor?.version ?? 0}`).join('|');
      if (signature !== entry.signature) {
        let offset = 0; mesh.boundingSphere!.makeEmpty();
        for (const s of group.sources) {
          mesh.instanceMatrix.array.set(s.instanceMatrix.array.subarray(0, s.count * 16), offset * 16);
          if (mesh.instanceColor) mesh.instanceColor.array.set(s.instanceColor!.array.subarray(0, s.count * 3), offset * 3);
          if (!s.boundingSphere) s.computeBoundingSphere();
          mesh.boundingSphere!.union(s.boundingSphere!); offset += s.count;
        }
        mesh.count = group.count;
        mesh.instanceMatrix.clearUpdateRanges(); mesh.instanceMatrix.addUpdateRange(0, group.count * 16); mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) { mesh.instanceColor.clearUpdateRanges(); mesh.instanceColor.addUpdateRange(0, group.count * 3); mesh.instanceColor.needsUpdate = true; }
        entry.signature = signature;
      }
      entry.last = this.generation;
      mesh.matrix.copy(group.owner.matrixWorld); mesh.matrixWorld.copy(group.owner.matrixWorld);
      scene.add(mesh); this.mounted.push(mesh);
      for (const s of group.sources) { s.visible = false; this.hidden.push(s); }
    }
    this.groups.clear();
    // A short turn-around cache; unloaded tiles/materials cannot remain held
    // indefinitely, and geometry/material lifetime stays with the source tiles.
    for (const entry of this.entries.values()) if (this.generation - entry.last > 2) {
      entry.release();
    }
  }

  end(): void {
    for (const mesh of this.mounted) mesh.removeFromParent(); this.mounted.length = 0;
    for (const mesh of this.hidden) mesh.visible = true; this.hidden.length = 0;
    this.groups.clear();
  }
  stats(): { batches: number; instanceBytes: number } {
    let instanceBytes = 0;
    for (const { mesh } of this.entries.values()) instanceBytes += mesh.instanceMatrix.array.byteLength + (mesh.instanceColor?.array.byteLength ?? 0);
    return { batches: this.entries.size, instanceBytes };
  }
  dispose(): void { this.end(); for (const entry of this.entries.values()) entry.release(); this.entries.clear(); }
}
